import type {
  CustomerCreateApiData,
  CustomerCreateRequestBody,
  MemberCreateScope,
  MemberCustomerStatus,
  MemberSearchApiData
} from '@/api/member'
import { isContextSimpleSignupEnabled, type ProjectContext } from '@/utils/projectContext'
import { isSignupPhoneKeyword } from '@/utils/phone'

export const MEMBER_NOT_FOUND_MESSAGE = '회원이 존재하지 않습니다.'

/** found=false 이고 createScope + EARN_POLICY 로 생성 가능한지 */
export function canCreateGuestCustomer(
  search: MemberSearchApiData,
  context: ProjectContext
): boolean {
  return !search.found && search.createScope != null && isContextSimpleSignupEnabled(context)
}

/** OWN/BOTH: 전화번호 확인창 후 생성 */
export function needsSignupPhoneConfirm(scope: MemberCreateScope): boolean {
  return scope === 'OWN' || scope === 'BOTH'
}

function getBrandPlatformProjectCode(context: ProjectContext): string {
  const parentCode = context.parentCode?.trim()
  if (!parentCode) {
    throw new Error('브랜드 프로젝트의 parentCode가 없습니다. 관리자에게 문의해 주세요.')
  }
  return parentCode
}

export function buildCustomerCreateRequest(
  context: ProjectContext,
  search: MemberSearchApiData,
  keyword: string
): CustomerCreateRequestBody {
  const scope = search.createScope
  if (!scope) {
    throw new Error('간편가입 scope가 없습니다.')
  }

  const body: CustomerCreateRequestBody = { scope }

  if (scope === 'OWN' || scope === 'BOTH') {
    body.phone = keyword.trim()
  }

  if (scope === 'BRAND' || scope === 'BOTH') {
    body.platformProjectCode = getBrandPlatformProjectCode(context)
  }

  if (scope === 'BRAND') {
    const platformCustomerCode = search.platformCustomerCode?.trim()
    if (!platformCustomerCode) {
      throw new Error('플랫폼 고객 정보가 없어 간편가입을 진행할 수 없습니다.')
    }
    body.platformCustomerCode = platformCustomerCode
  }

  return body
}

export function buildSimpleSignupConfirmMessage(phone: string): string {
  return `${phone} 로 간편가입을 진행하시겠습니까?`
}

/** 생성 API 응답 → 회원 대시보드 state */
export function resolveNavigateAfterCreate(
  scope: MemberCreateScope,
  search: MemberSearchApiData,
  data: CustomerCreateApiData
): { customerCode: string; nickname?: string; status?: MemberCustomerStatus } {
  const status = search.status ?? 'ACTIVE'
  if (scope === 'OWN') {
    const own = data.ownProjectCustomer
    if (!own?.code) {
      throw new Error('간편가입 후 회원 정보를 불러올 수 없습니다.')
    }
    return { customerCode: own.code, nickname: search.nickname, status }
  }

  const brand = data.brandProjectCustomer
  if (!brand?.code) {
    throw new Error('간편가입 후 회원 정보를 불러올 수 없습니다.')
  }

  if (scope === 'BOTH') {
    return {
      customerCode: brand.code,
      nickname: data.ownProjectCustomer?.nickname,
      status
    }
  }

  return { customerCode: brand.code, nickname: search.nickname, status }
}
